import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Heart, MessageCircle, Image as ImageIcon, Send, Loader2, Video } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { it as itLocale } from "date-fns/locale";

const Avatar = ({ user, size = 44 }) => {
    const url = user?.profile?.avatar_url;
    return (
        <div className="rounded-full overflow-hidden border-2 border-gold bg-surface flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
            {url ? <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" /> : <span className="text-ink text-sm font-medium">{user?.full_name?.[0]?.toUpperCase()}</span>}
        </div>
    );
};

const timeAgo = (d) => {
    try { return formatDistanceToNow(new Date(d), { addSuffix: true, locale: itLocale }); } catch { return ""; }
};

const Post = ({ post, onUpdate }) => {
    const [showComments, setShowComments] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [busy, setBusy] = useState(false);

    const like = async () => {
        try {
            await api.post(`/posts/${post.id}/like`);
            onUpdate();
        } catch {}
    };
    const sendComment = async () => {
        if (!commentText.trim()) return;
        setBusy(true);
        try {
            await api.post(`/posts/${post.id}/comment`, { text: commentText });
            setCommentText("");
            onUpdate();
        } finally { setBusy(false); }
    };

    return (
        <article className="bg-paper rounded-2xl border border-border-line soft-shadow-sm p-6 fade-up" data-testid={`post-${post.id}`}>
            <header className="flex items-center gap-3 mb-4">
                <Link to={`/profile/${post.author?.id}`}><Avatar user={post.author} /></Link>
                <div className="flex-1">
                    <Link to={`/profile/${post.author?.id}`} className="font-medium text-ink hover:underline">{post.author?.full_name}</Link>
                    <div className="text-xs text-muted">{post.author?.profile?.headline} · {timeAgo(post.created_at)}</div>
                </div>
            </header>
            <p className="text-ink whitespace-pre-wrap leading-relaxed">{post.text}</p>
            {post.media_url && post.media_type === "image" && (
                <img src={mediaUrl(post.media_url)} alt="" className="mt-4 w-full max-h-[500px] object-cover rounded-xl border border-border-line" />
            )}
            {post.media_url && post.media_type === "video" && (
                <video src={mediaUrl(post.media_url)} controls className="mt-4 w-full max-h-[500px] rounded-xl border border-border-line" />
            )}
            <div className="flex items-center gap-6 mt-5 pt-4 border-t border-border-line text-sm">
                <button data-testid={`like-${post.id}`} onClick={like} className={`flex items-center gap-1.5 transition-colors ${post.liked_by_me ? "text-bordeaux" : "text-muted hover:text-bordeaux"}`}>
                    <Heart size={18} fill={post.liked_by_me ? "currentColor" : "none"} /> {post.like_count}
                </button>
                <button onClick={() => setShowComments(!showComments)} className="flex items-center gap-1.5 text-muted hover:text-ink transition-colors">
                    <MessageCircle size={18} /> {post.comment_count}
                </button>
            </div>
            {showComments && (
                <div className="mt-4 space-y-3">
                    {post.comments?.map((c) => (
                        <div key={c.id} className="flex gap-2">
                            <Avatar user={{ profile: { avatar_url: c.avatar_url }, full_name: c.user_name }} size={32} />
                            <div className="bg-surface rounded-2xl px-4 py-2 flex-1">
                                <div className="text-xs font-medium text-ink">{c.user_name}</div>
                                <div className="text-sm text-ink">{c.text}</div>
                            </div>
                        </div>
                    ))}
                    <div className="flex gap-2 mt-2">
                        <input data-testid={`comment-input-${post.id}`} value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment()} placeholder="Scrivi un commento…" className="input-clean flex-1" />
                        <button data-testid={`comment-send-${post.id}`} onClick={sendComment} disabled={busy} className="btn-primary px-4"><Send size={14} /></button>
                    </div>
                </div>
            )}
        </article>
    );
};

const Composer = ({ onPosted }) => {
    const { user } = useAuth();
    const [text, setText] = useState("");
    const [media, setMedia] = useState(null);
    const [mediaType, setMediaType] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [posting, setPosting] = useState(false);
    const fileRef = useRef(null);

    const upload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setMedia(data.url);
            setMediaType(f.type.startsWith("video") ? "video" : "image");
        } catch { toast.error("Upload fallito"); }
        finally { setUploading(false); }
    };

    const post = async () => {
        if (!text.trim() && !media) return;
        setPosting(true);
        try {
            await api.post("/posts", { text, media_url: media, media_type: mediaType });
            setText(""); setMedia(null); setMediaType(null);
            onPosted();
            toast.success("Pubblicato");
        } catch { toast.error("Errore"); }
        finally { setPosting(false); }
    };

    return (
        <div className="bg-paper rounded-2xl border border-border-line soft-shadow-sm p-5 mb-6">
            <div className="flex gap-3">
                <Avatar user={user} />
                <textarea data-testid="composer-text" value={text} onChange={(e) => setText(e.target.value)} placeholder={`Cosa vuoi condividere, ${user?.full_name?.split(" ")[0]}?`} className="input-clean flex-1 min-h-[80px] resize-none" />
            </div>
            {media && (
                <div className="mt-3 relative">
                    {mediaType === "image" ? <img src={mediaUrl(media)} alt="" className="rounded-xl max-h-64 object-cover" /> : <video src={mediaUrl(media)} controls className="rounded-xl max-h-64" />}
                    <button onClick={() => { setMedia(null); setMediaType(null); }} className="absolute top-2 right-2 bg-ink/80 text-white text-xs px-2 py-1 rounded-full">Rimuovi</button>
                </div>
            )}
            <div className="flex items-center justify-between mt-4">
                <div className="flex gap-2">
                    <button data-testid="composer-image" onClick={() => fileRef.current?.click()} className="btn-ghost text-sm flex items-center gap-1.5 text-muted hover:text-ink">
                        {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />} Foto/Video
                    </button>
                    <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={upload} />
                </div>
                <button data-testid="composer-publish" onClick={post} disabled={posting || (!text.trim() && !media)} className="btn-primary text-sm">
                    {posting ? "Pubblicazione…" : "Pubblica"}
                </button>
            </div>
        </div>
    );
};

const Feed = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        try {
            const { data } = await api.get("/feed");
            setPosts(data);
        } catch {}
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="max-w-2xl mx-auto" data-testid="feed-page">
            <div className="mb-6">
                <h1 className="font-heading text-4xl">Il tuo feed</h1>
                <p className="text-sm text-muted mt-1">Le voci della rete, in tempo reale.</p>
            </div>
            <Composer onPosted={load} />
            {loading && <div className="text-center py-10 text-muted">Caricamento…</div>}
            {!loading && posts.length === 0 && (
                <div className="text-center py-12 bg-paper rounded-2xl border border-border-line">
                    <div className="font-heading text-2xl">Inizia tu</div>
                    <p className="text-sm text-muted mt-2">Pubblica il primo post per accendere la conversazione.</p>
                </div>
            )}
            <div className="space-y-6">
                {posts.map((p) => <Post key={p.id} post={p} onUpdate={load} />)}
            </div>
        </div>
    );
};

export default Feed;
